/**
 * RU — the default locale and the FALLBACK for every other one (docs/06 §8: `tk → ru`,
 * `en → ru`, missing key → RU string). RU is therefore the only locale that is guaranteed
 * complete, and every new key must land here first.
 *
 * Key scheme: `screen.element` inside the `producer` namespace.
 *
 * Note on content: product and profile CONTENT is never translated (decision D4) — names,
 * descriptions and composition render as the producer entered them (Russian) in all three UI
 * languages. Only the chrome is localized. Category names are the exception and come from the
 * API per-locale (`name_ru`/`name_en`/`name_tk`), not from these bundles.
 */
export const ru = {
  common: {
    appName: 'MOST',
    cabinet: 'Кабинет производителя',
    save: 'Сохранить',
    saving: 'Сохранение…',
    saved: 'Сохранено',
    cancel: 'Отмена',
    back: 'Назад',
    next: 'Далее',
    close: 'Закрыть',
    edit: 'Редактировать',
    delete: 'Удалить',
    retry: 'Повторить',
    loading: 'Загрузка…',
    yes: 'Да',
    no: 'Нет',
    required: 'Обязательное поле',
    optional: 'необязательно',
    error: 'Ошибка',
    errorGeneric: 'Что-то пошло не так. Попробуйте ещё раз.',
    errorNetwork: 'Нет связи с сервером. Проверьте подключение.',
    errorLoad: 'Не удалось загрузить данные.',
    logout: 'Выйти',
    language: 'Язык',
    of: 'из',
    page: 'Страница',
    unsavedTitle: 'Несохранённые изменения',
    unsavedBody: 'Вы внесли изменения, которые не сохранены. Покинуть страницу?',
    unsavedLeave: 'Уйти без сохранения',
    unsavedStay: 'Остаться',
  },

  units: {
    piece: 'штука',
    pack: 'пачка',
    box: 'упаковка',
    pallet: 'палета',
    truck: 'фура',
    piece_plural: 'штук',
    pack_plural: 'пачек',
    box_plural: 'упаковок',
    pallet_plural: 'палет',
    truck_plural: 'фур',
    kg: 'кг',
  },

  nav: {
    home: 'Кабинет',
    profile: 'Профиль',
    products: 'Товары',
    orders: 'Заказы',
    notifications: 'Уведомления',
  },

  status: {
    draft: 'Черновик',
    active: 'Активен',
    archived: 'В архиве',
    awaitingPricing: 'Ожидает наценки MOST',
    order: {
      new: 'Новый',
      in_progress: 'В работе',
      completed: 'Завершён',
      cancelled: 'Отменён',
    },
  },

  // ── PRO-1 ────────────────────────────────────────────────────────────────────────────────
  home: {
    title: 'Кабинет',
    greeting: 'Добро пожаловать в кабинет производителя',

    // Status banner. There is NO `rejected` status — the banner is driven by `rejection_reason`.
    pendingTitle: 'Профиль ожидает одобрения MOST',
    pendingBody:
      'Мы проверяем ваш профиль. Пока идёт проверка, вы можете заполнить профиль и подготовить товары — они станут видны покупателям после одобрения.',
    rejectedTitle: 'MOST просит доработать профиль',
    rejectedBody: 'Комментарий MOST:',
    rejectedCta: 'Исправить профиль и отправить снова',

    stats: {
      activeProducts: 'Активных товаров',
      draftProducts: 'Черновиков',
      newOrders: 'Новых заказов',
    },

    telegram: {
      title: 'Telegram',
      linked: 'Telegram подключён ✓',
      linkedSince: 'Подключён {{date}}',
      notLinked: 'Telegram не подключён',
      // Silent fallback — an unlinked Telegram is NEVER an error, just a different channel.
      fallbackHint:
        'Без Telegram уведомления о заказах будут приходить на email и сюда, в кабинет.',
      connect: 'Подключить Telegram',
      connecting: 'Открываем Telegram…',
      unlink: 'Отключить',
      waiting: 'Ожидаем подтверждения в Telegram… Не закрывайте эту вкладку.',
      openFailed: 'Не удалось создать ссылку. Попробуйте ещё раз.',
      why: 'Мгновенные оповещения о новых заказах прямо в мессенджер.',
    },
  },

  // ── PRO-2 ────────────────────────────────────────────────────────────────────────────────
  profile: {
    title: 'Профиль',
    subtitle: 'Здесь видно, какие данные показываются покупателям, а какие — только MOST.',

    publicSection: 'Видно покупателям',
    publicHint: 'Эти поля видит любой покупатель в каталоге.',
    privateSection: 'Видно только MOST',
    privateHint: 'Покупатели этого не видят никогда. Эти данные нужны MOST для работы с вами.',

    brandName: 'Бренд',
    country: 'Страна',
    description: 'Описание производства',
    productionCapacity: 'Производственная мощность',
    rating: 'Рейтинг',
    ratingNone: 'Рейтинг ещё не выставлен',
    ratingHint: 'Рейтинг выставляет MOST. Изменить его нельзя.',

    legalName: 'Юридическое название',
    address: 'Адрес',
    contactPerson: 'Контактное лицо',
    phone: 'Телефон',

    markets: 'Экспортные рынки',
    marketsHint:
      'Покупатели не видят ваши экспортные рынки ни в каком виде — ни контакты представителей, ни даже список стран.',
    marketAdd: 'Добавить рынок',
    marketRemove: 'Убрать рынок',
    marketCountry: 'Страна',
    marketType: 'Тип',
    marketCanExport: 'Можем экспортировать',
    marketAlreadyExports: 'Уже экспортируем',
    marketRep: 'Контакт представителя',
    marketRepHint: 'Самое чувствительное поле. Не показывается покупателям ни при каких условиях.',
    marketDuplicate: 'Такая пара «страна + тип» уже добавлена',

    certificates: 'Сертификаты',
    certificatesHint: 'Видны покупателям. PDF, JPEG, PNG или WEBP, до 10 МБ. Не более {{max}} шт.',
    certificateTitle: 'Название сертификата',
    certificateAdd: 'Загрузить сертификат',
    certificateDelete: 'Удалить сертификат',
    certificateDeleteConfirm: 'Удалить сертификат «{{title}}»?',

    submitForApproval: 'Отправить на одобрение',
    submitHint:
      'После отправки MOST проверит профиль. Обязательны бренд и страна.',
    submitted: 'Профиль отправлен на одобрение MOST.',
    lockedTitle: 'Только для MOST',
  },

  // ── PRO-3 ────────────────────────────────────────────────────────────────────────────────
  products: {
    title: 'Товары',
    add: 'Добавить товар',
    filterAll: 'Все',
    thumb: 'Фото',
    name: 'Название',
    category: 'Категория',
    status: 'Статус',
    basePrice: 'Ваша цена',
    perPiece: 'за штуку',
    actions: 'Действия',
    activate: 'Активировать',
    archive: 'В архив',
    archiveConfirm: 'Отправить товар «{{name}}» в архив?',
    archiveBody: 'Товар исчезнет из каталога покупателей. Вернуть его можно позже.',
    activateConfirm: 'Активировать товар «{{name}}»?',
    activateBody:
      'Товар будет отправлен в MOST на установку цены. Покупатели увидят его только после того, как MOST установит цену каталога.',
    activated: 'Товар отправлен в MOST на установку цены.',

    // The is_priced boolean — NEVER a price.
    isPricedYes: 'В каталоге',
    isPricedNo: 'Ожидает наценки MOST',
    isPricedHint:
      'Товар появится в каталоге покупателей, как только MOST установит цену каталога.',

    emptyTitle: 'Товаров пока нет',
    emptyBody: 'Добавьте первый товар — он появится в каталоге после проверки MOST.',
    emptyCta: 'Добавить первый товар',
    lockedTitle: 'Товары станут доступны после одобрения профиля',
    lockedBody:
      'Заполните профиль и отправьте его на одобрение MOST. После одобрения вы сможете добавлять товары.',
    lockedCta: 'Перейти к профилю',
  },

  // ── PRO-4 ────────────────────────────────────────────────────────────────────────────────
  wizard: {
    titleNew: 'Новый товар',
    titleEdit: 'Редактирование товара',
    draftSaved: 'Черновик сохранён',
    draftSaving: 'Сохраняем черновик…',
    draftFailed: 'Не удалось сохранить черновик. Данные не потеряны — попробуйте ещё раз.',

    step1: 'Основное',
    step2: 'Упаковка',
    step3: 'Цена',
    step4: 'Фото и видео',
    step5: 'Проверка',

    // Step 1
    name: 'Название товара',
    nameHint: 'Как на этикетке. Покупатели увидят название так, как вы его введёте.',
    category: 'Категория',
    categoryPlaceholder: 'Выберите категорию',
    categoryLoadFailed: 'Не удалось загрузить категории.',
    description: 'Описание',
    descriptionHint: 'Например: «сок клубничный, 15 %»',
    composition: 'Состав',
    tnved: 'Код ТНВЭД',
    tnvedHint: 'Код таможенной классификации',
    countryOfOrigin: 'Страна происхождения',
    capacity: 'Мощность в месяц',
    capacityValue: 'Сколько',
    capacityUnit: 'В чём',

    // Step 2
    packagingTitle: 'Цепочка упаковки',
    packagingHint:
      'Опишите, как товар упакован: сколько штук в пачке, пачек в упаковке, упаковок на палете и палет в фуре.',
    unitWeight: 'Вес одной штуки, кг',
    unitWeightHint: 'Брутто, за штуку',
    piecesPerPack: 'Штук в пачке',
    packsPerBox: 'Пачек в упаковке',
    boxesPerPallet: 'Упаковок на палете',
    palletsPerTruck: 'Палет в фуре',
    palletsPerTruckHint:
      'Сколько палет ИМЕННО ЭТОГО товара помещается в стандартную фуру — зависит от того, как он штабелируется.',
    chainSummary: '1 фура = {{pallets}} палет = {{boxes}} упаковок = {{packs}} пачек = {{pieces}} штук',
    chainIncomplete: 'Заполните все поля цепочки, чтобы увидеть итог',

    // Step 3
    priceTitle: 'Ваша цена',
    priceValue: 'Цена, USD',
    priceUnit: 'За единицу',
    pricePreview: '≈ {{price}} за штуку',
    pricePreviewHint: 'Так ваша цена пересчитывается на одну штуку.',
    priceIncomplete: 'Введите цену и заполните цепочку упаковки, чтобы увидеть пересчёт',
    // The reassurance that makes the whole model work for the producer.
    priceNotice: 'MOST устанавливает финальную цену каталога; покупатели вашу цену не видят.',

    // Step 4
    mediaTitle: 'Фото и видео',
    photos: 'Фотографии',
    photosHint:
      'До {{max}} фото: JPEG, PNG или WEBP, до {{size}}. Первое фото — главное. Перетащите, чтобы поменять порядок.',
    photoSlot: 'Слот {{n}}',
    slotFront: 'спереди',
    slotBack: 'сзади',
    slotLeft: 'слева',
    slotRight: 'справа',
    slotTop: 'сверху',
    slotBottom: 'снизу',
    slotExtra: 'дополнительно',
    slotHintAdvisory: 'Подписи — подсказка, а не требование.',
    video: 'Видео',
    videoHint: 'Одно видео MP4, до {{size}}. Необязательно.',
    dropHere: 'Перетащите файлы сюда',
    orPick: 'или выберите на компьютере',
    uploadRetry: 'Повторить загрузку',
    uploadFailed: 'Не удалось загрузить',
    tooLarge: 'Файл больше {{size}}',
    badType: 'Неподдерживаемый формат файла',
    needPhoto: 'Нужна хотя бы одна фотография',
    photoDeleteConfirm: 'Удалить это фото?',
    photoDeleteLast: 'У активного товара нельзя удалить последнее фото.',

    // Step 5
    reviewTitle: 'Проверка',
    reviewHint: 'Проверьте данные перед отправкой в MOST.',
    activate: 'Активировать',
    activateBlocked: 'Чтобы активировать товар, заполните все обязательные поля и добавьте фото.',
    activateSuccess: 'Отправлено в MOST на установку цены',
    activateSuccessBody:
      'Товар появится в каталоге покупателей после того, как MOST установит цену каталога.',
    saveDraft: 'Сохранить черновик',
    saveOnly: 'Сохранить',

    // Margin guard — phrased as a re-pricing consequence. NEVER as "margin", and the selling
    // price is never mentioned because we do not have it (docs/04 §1.2).
    repriceWarnTitle: 'Товар вернётся в MOST на пересчёт цены',
    repriceWarnBody:
      'Вы меняете цену или упаковку у товара, который уже опубликован в каталоге. После сохранения MOST заново установит цену каталога.',
    repriceWarnHidden:
      'Если новая цена окажется не ниже текущей цены каталога, товар будет скрыт от покупателей, пока MOST не установит новую цену.',
    repriceWarnConfirm: 'Понятно, сохранить',
  },

  // ── PRO-5 / PRO-6 ────────────────────────────────────────────────────────────────────────
  orders: {
    title: 'Заказы',
    // Wording is careful: the producer sees THEIR PART of an order, never the order.
    subtitle: 'Здесь показаны только ваши позиции из каждого заказа.',
    number: 'Номер',
    date: 'Дата',
    status: 'Статус',
    buyerCountry: 'Страна покупателя',
    sliceTotal: 'Сумма по вашим позициям',

    emptyTitle: 'Заказов пока нет',
    emptyBody: 'Они появятся здесь, когда покупатель закажет ваши товары.',

    detailTitle: 'Заказ {{number}}',
    yourItems: 'Ваши позиции',
    product: 'Товар',
    qty: 'Количество',
    pieces: 'Штук',
    pricePerPiece: 'Ваша цена за штуку',
    lineTotal: 'Сумма',
    lineWeight: 'Вес',
    sliceWeight: 'Вес ваших позиций',
    totalAtYourPrices: 'Итого по вашим ценам',

    // The fixed instruction (openapi: `instruction: contact_most` — an i18n key, not a string).
    instruction_contact_most: 'Свяжитесь с MOST по заказу {{number}}.',
    instructionHint: 'MOST ведёт переговоры с покупателем и сообщит детали.',
    // The persistent explanation of why so little is shown — turns absence into trust.
    anonymityNote:
      'MOST не раскрывает покупателя производителю, а вашу цену — покупателю. Поэтому здесь показаны только ваши позиции и ваши цены.',

    notFound: 'Заказ не найден',
    notFoundBody: 'Возможно, в этом заказе нет ваших товаров.',
  },

  // ── PRO-7 ────────────────────────────────────────────────────────────────────────────────
  notifications: {
    title: 'Уведомления',
    markAllRead: 'Отметить все как прочитанные',
    markRead: 'Отметить прочитанным',
    unread: 'Непрочитанное',
    loadMore: 'Показать ещё',
    emptyTitle: 'Уведомлений нет',
    emptyBody: 'Здесь появятся сообщения о заказах и решениях MOST.',

    // Inline detail (PRO-7). The order payload already carries this producer's own slice,
    // so the row can expand without a second request.
    expand: 'Подробнее',
    collapse: 'Свернуть',
    openOrder: 'Открыть заказ',

    // Notification types (docs/04 §6). These keys MUST match the backend `Type` constants
    // in backend/internal/notifications/domain.go — that file states the constants ARE the
    // i18n keys, so a mismatch silently degrades the row to `generic`.
    // `product_priced` deliberately carries NO price.
    type: {
      order_created_producer: 'Новый заказ',
      order_created_producer_body: 'Покупатель заказал ваши товары. Заказ {{order_number}}.',
      order_status_changed: 'Статус заказа изменён',
      order_status_changed_body: 'Заказ {{order_number}} — новый статус: {{status}}.',
      product_priced: 'Товар опубликован',
      product_priced_body: 'MOST установил цену каталога — товар «{{product_name}}» виден покупателям.',
      producer_approved: 'Профиль одобрен',
      producer_approved_body: 'MOST одобрил ваш профиль. Теперь вы можете добавлять товары.',
      producer_rejected: 'MOST просит доработать профиль',
      producer_rejected_body: 'Откройте профиль, чтобы прочитать комментарий и отправить снова.',
      generic: 'Уведомление',
    },
  },

  errors: {
    // Mapped from the RFC 9457 `type` suffix. We NEVER render `detail` — it is English.
    'validation-failed': 'Проверьте правильность заполнения полей.',
    'not-found': 'Не найдено.',
    forbidden: 'Недостаточно прав.',
    conflict: 'Данные изменились. Обновите страницу и попробуйте снова.',
    'rate-limited': 'Слишком много попыток. Подождите немного.',
    'payload-too-large': 'Файл слишком большой.',
    'unsupported-media-type': 'Неподдерживаемый формат файла.',
    // Field-level codes from `Problem.errors[].code`.
    field: {
      required: 'Обязательное поле',
      invalid: 'Неверное значение',
      too_short: 'Слишком короткое значение',
      too_long: 'Слишком длинное значение',
      duplicate: 'Такое значение уже есть',
      min: 'Значение слишком маленькое',
      max: 'Значение слишком большое',
    },
  },

  validation: {
    required: 'Обязательное поле',
    minLength: 'Минимум {{n}} символов',
    integerMin1: 'Целое число, не меньше 1',
    positive: 'Должно быть больше нуля',
    number: 'Введите число',
    country2: 'Код страны из двух букв',
    price: 'Введите цену, например 8.40',
  },
  chat: {
    title: 'Поддержка MOST',
    subtitle: 'Мы на связи — товары, цены, заказы',
    open: 'Открыть чат поддержки',
    close: 'Закрыть чат',
    placeholder: 'Напишите сообщение…',
    send: 'Отправить',
    attach: 'Прикрепить файл',
    record: 'Записать голосовое сообщение',
    stopRecording: 'Остановить запись',
    loadOlder: 'Показать более ранние',
    empty: 'Напишите нам — поможем с товарами, ценами и заказами.',
    sendError: 'Не удалось отправить. Попробуйте ещё раз.',
    call: {
      audio: 'Аудиозвонок в поддержку',
      video: 'Видеозвонок в поддержку',
      incoming: 'Входящий звонок от поддержки',
      outgoing: 'Звоним в поддержку…',
      connecting: 'Соединение…',
      active: 'Идёт разговор',
      endedStatus: 'Звонок завершён',
      accept: 'Ответить',
      decline: 'Отклонить',
      hangUp: 'Завершить',
      completed: 'Звонок',
      missed: 'Пропущенный звонок',
      declined: 'Звонок отклонён',
      mediaDenied: 'Нет доступа к микрофону или камере',
      connectionLost: 'Связь потеряна',
      failed: 'Не удалось начать звонок',
    },
  },
} as const;

export type Translations = typeof ru;
